// THE GOLDEN-CSV DROP POINT.
//
// Drag files in (or pick them), tag each one with what it IS, drop. The period and as-of date
// come from the cycle — the owner never retypes them, so a file can never be landed against
// the wrong period by a typing slip.
//
// WHAT THIS SURFACE OWES THE OWNER, and why each piece is here:
//
//   * ONE FILE, TWO KINDS. Portfolio returns and benchmark returns arrive in the SAME FE
//     bytes; the contract's answer (golden-csv-contract-v1 §8.1, DECISION-2) is to drop those
//     bytes twice, once per kind — identical sha256 is the proof it is one file. So a file can
//     be queued more than once, and a landed row offers "drop again as another kind".
//
//   * A REJECTION IS NOT A FAILURE OF THE UI. The ingest runs seven HARD gates and refuses
//     the whole file on any of them (wrong quarter, wrong currency, rolling-source marker, a
//     Name that collides with another). The gate NAME and its detail are the only things that
//     tell the owner what to fix, so they are shown verbatim, the row stays on screen, and it
//     is re-droppable in place.
//
//   * RE-DROPPING IS SAFE. UNIQUE(period_label, kind, content_sha256) makes the drop
//     idempotent: the same bytes for the same period and kind come back "already landed" with
//     nothing re-parsed. That is stated on the row so nobody hesitates to retry.
//
//   * FLAGS ARE NOT ERRORS. A parsed file can still raise flags (a benchmark ARC has not
//     published yet, an FE Name with no crosswalk). They are shown with their severity and,
//     crucially, whether they BLOCK THE GATE — that is the difference between "note it" and
//     "this cycle cannot be signed until it is resolved".
//
//   * THE FILE SAYS WHAT IT IS — SO DON'T ASK. Hand-classifying every export in the "What is
//     this file?" dropdown was busywork the file itself could answer, and the owner said so:
//     *"Can you not work that out yourself???"* On selection we read the HEAD of each file and
//     run detectGoldenKind (src/lib/investment-reporting/detectKind.ts), which reads the header
//     row, the FE Performance Table preamble and the entity_ref/metric vocabulary.
//     IT SUGGESTS; IT NEVER DECIDES. The dropdown stays, stays freely overridable, and a
//     suggestion is only ever written into a row the human has not tagged. The kind is part of
//     a file's identity server-side — UNIQUE(period_label, kind, content_sha256) — so a wrong
//     kind lands real bytes in the wrong bucket under a hash that looks legitimately ingested.
//     Hence: 'certain' pre-selects and says why, 'likely' pre-selects but is visibly marked as a
//     guess, and 'ambiguous' selects NOTHING and says what was ambiguous. Nothing auto-submits —
//     the human still presses the button.
//
//   * `allowedKinds` NARROWS WHAT THIS SURFACE CAN LAND, AND IT IS A GATE, NOT A HINT. Since the
//     combined export became THE intake (CombinedCsvDropZone), this component survives only for
//     the three blotter files that carry no FE preamble. Left unrestricted its picker still
//     offered fe_portfolio_returns / fe_benchmark_returns — so a combined CSV that
//     CombinedCsvDropZone REFUSES (a missing peer house, an incomplete benchmark ladder) could be
//     re-tagged here and landed with no peer check, no tie check, and no record that a refusal had
//     been overridden. `allowedKinds` closes that: it filters the picker, it filters the
//     detector's pre-selection AND its one-click alternatives, and it filters the upload queue, so
//     an out-of-scope kind cannot reach the ingest by any of the three routes into `kind`. When a
//     file is detected as an out-of-scope kind the row says so and points at the surface that owns
//     it, rather than asserting a kind the picker does not offer. An FE-kind override, if it is
//     ever genuinely wanted, has to be its own named and separately confirmed action that writes
//     the override and its reason onto reporting_golden_sources — not a quiet entry in this list.

import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  FileUp,
  HelpCircle,
  Info,
  RotateCcw,
  Sparkles,
  UploadCloud,
  X,
} from "lucide-react";
import {
  GOLDEN_BASES,
  GOLDEN_KIND_SPECS,
  goldenKindLabel,
  goldenKindSpec,
  ingestSeverityTone,
  toneText,
  type GoldenKindSpec,
} from "./labels";
import {
  localDropError,
  readCsvPayload,
  useGoldenDrop,
  type GoldenDropOutcome,
  type GoldenIngestFlag,
} from "@/hooks/investment-reporting/useGoldenIngest";
import { PERIOD_LABEL_RX } from "@/lib/investment-reporting/period";
import {
  DETECT_HEAD_BYTES,
  detectGoldenKind,
  type KindDetection,
} from "@/lib/investment-reporting/detectKind";

interface QueuedDrop {
  id: string;
  file: File;
  /**
   * "" until the kind is settled. It can be PRE-FILLED by the detector, but only while it is
   * still "" — a human choice always wins, and an ambiguous detection leaves it "".
   */
  kind: string;
  status: "queued" | "reading" | "uploading" | "done";
  outcome: GoldenDropOutcome | null;
  /** What the detector made of the file's head. null until it has run (or if it threw). */
  detection: KindDetection | null;
  detecting: boolean;
}

// Read only the HEAD of the file to identify it. The identity lives entirely at the top — the
// header row, the FE Performance Table preamble, the first entity_ref values — and these are
// large exports, so pulling the whole thing into memory to answer "what is this?" is waste.
// `partial` matters: it tells the detector that an ABSENCE in the sample proves nothing.
async function detectFileKind(file: File): Promise<KindDetection> {
  const head = await file.slice(0, DETECT_HEAD_BYTES).text();
  return detectGoldenKind(file.name, head, { partial: file.size > DETECT_HEAD_BYTES });
}

let dropSeq = 0;
function nextId(): string {
  dropSeq += 1;
  return `drop-${dropSeq}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function csvFormatLabel(f: string | null): string {
  if (f === "fe_performance_table") return "FE Performance Table";
  if (f === "generic_long") return "Generic long CSV";
  return f ?? "—";
}

// ----------------------------------------------------------------------------
// One flag raised by a parsed drop.
// ----------------------------------------------------------------------------
function IngestFlagLine({ flag }: { flag: GoldenIngestFlag }) {
  const tone = flag.blocks_gate ? "bad" : ingestSeverityTone(flag.severity);
  return (
    <li className="flex items-start gap-1.5">
      <AlertTriangle className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", toneText(tone))} aria-hidden />
      <span className="text-[11px] text-muted-foreground">
        <span className="text-foreground">{flag.check_name ?? flag.kind ?? "check"}</span>
        {flag.severity ? ` · ${flag.severity}` : ""}
        {flag.blocks_gate ? (
          <span className={cn("ml-1", toneText("bad"))}>blocks the gate</span>
        ) : (
          <span className="ml-1">advisory — does not block the gate</span>
        )}
        {flag.blocks_gate && flag.human_resolvable ? " · resolvable on the Flags tab" : ""}
      </span>
    </li>
  );
}

// ----------------------------------------------------------------------------
// What the detector made of one file, in plain English.
//
// The three confidences render DIFFERENTLY on purpose, because they mean different things to
// the person about to press the button:
//   certain    — quiet, stated as fact, with the evidence.
//   likely     — amber and explicitly labelled a guess. It IS pre-selected, so the only thing
//                standing between a wrong guess and a wrong bucket is that it looks like a guess.
//   ambiguous  — nothing selected, and the shortlist offered as one-click choices. Offering the
//                shortlist is help; picking from it is not ours to do.
//
// `isAllowed` is the same gate the picker uses. A detection the picker cannot offer is NOT
// rendered as a suggestion — asserting "Detected: Benchmark returns … change it above if that is
// wrong" when the list above holds no such entry would read as a UI fault and invite the human to
// go looking for the way round. It renders as a redirection instead.
// ----------------------------------------------------------------------------
function DetectionLine({
  detecting,
  detection,
  kind,
  disabled,
  isAllowed,
  onPick,
}: {
  detecting: boolean;
  detection: KindDetection | null;
  kind: string;
  disabled: boolean;
  isAllowed: (kind: string) => boolean;
  onPick: (kind: string) => void;
}) {
  if (detecting) {
    return (
      <p className="font-body text-[11px] text-muted-foreground">
        Reading the file to work out what it is…
      </p>
    );
  }
  if (!detection) return null;

  // Detected as something this surface does not land. Name it, say where it belongs, and offer
  // nothing — the alternatives shortlist is suppressed too, or an out-of-scope kind would arrive
  // by one click of a button we drew ourselves.
  if (detection.kind && !isAllowed(detection.kind) && !kind) {
    return (
      <p className="flex items-start gap-1.5 font-body text-[11px] text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden />
        <span>
          <span className="text-foreground">
            This looks like {goldenKindLabel(detection.kind)} — an FE performance export.
          </span>{" "}
          It is not landed from here. Drop it on the combined export at the top of this tab, which
          classifies every row, reconciles the benchmark ladder and shows you the result before
          anything is uploaded.
        </span>
      </p>
    );
  }

  if (detection.confidence === "ambiguous") {
    const offers = detection.alternatives.filter(isAllowed);
    return (
      <div className="space-y-1">
        <p className="flex items-start gap-1.5 font-body text-[11px] text-muted-foreground">
          <HelpCircle className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden />
          <span>
            <span className="text-foreground">Could not tell what this file is.</span>{" "}
            {detection.reason}
          </span>
        </p>
        {offers.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pl-5">
            {offers.map((k) => (
              <Button
                key={k}
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px]"
                disabled={disabled}
                onClick={() => onPick(k)}
              >
                {goldenKindLabel(k)}
              </Button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // The human has moved it off the suggestion. Stop asserting the suggestion — say whose
  // answer is going to be used.
  if (kind && kind !== detection.kind) {
    return (
      <p className="flex items-start gap-1.5 font-body text-[11px] text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden />
        <span>
          Detected {goldenKindLabel(detection.kind)}, but you have set it to{" "}
          <span className="text-foreground">{goldenKindLabel(kind)}</span> — your choice is what
          gets used.
        </span>
      </p>
    );
  }

  const guessing = detection.confidence === "likely";
  return (
    <p
      className={cn(
        "flex items-start gap-1.5 font-body text-[11px]",
        guessing ? toneText("attention") : "text-muted-foreground",
      )}
    >
      {guessing ? (
        <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden />
      ) : (
        <Sparkles className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden />
      )}
      <span>
        <span className="text-foreground">
          {guessing ? "Best guess" : "Detected"}: {goldenKindLabel(detection.kind)}.
        </span>{" "}
        {detection.reason}{" "}
        {guessing
          ? "This one is a guess, not a certainty — give it a glance before you drop it."
          : "Change it above if that is wrong."}
      </span>
    </p>
  );
}

// ----------------------------------------------------------------------------
// The result panel for one dropped file.
// ----------------------------------------------------------------------------
function DropOutcomePanel({ outcome }: { outcome: GoldenDropOutcome }) {
  if (outcome.state === "parsed") {
    return (
      <div className="space-y-1.5 border-t pt-2">
        <div className="flex items-center gap-1.5 text-xs">
          <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden />
          <span className="text-primary font-medium">Parsed</span>
          <span className="text-muted-foreground">
            {outcome.rows_inserted != null ? `${outcome.rows_inserted} rows` : "landed"}
            {outcome.events_inserted ? ` · ${outcome.events_inserted} holdings events` : ""}
            {` · ${csvFormatLabel(outcome.csv_format)}`}
          </span>
        </div>

        {outcome.sha256 && (
          <p className="text-[11px] text-muted-foreground font-mono" title={outcome.sha256}>
            sha256 {outcome.sha256.slice(0, 16)}…
          </p>
        )}

        {outcome.fe && (
          <p className="text-[11px] text-muted-foreground">
            FE window {outcome.fe?.period?.window_start ?? "—"} → {outcome.fe?.period?.window_end ?? "—"}
            {Array.isArray(outcome.fe?.roster_missing) && outcome.fe.roster_missing.length > 0
              ? ` · ${outcome.fe.roster_missing.length} expected series absent (landed as not-found, never zero)`
              : ""}
            {Array.isArray(outcome.fe?.unmapped) && outcome.fe.unmapped.length > 0
              ? ` · ${outcome.fe.unmapped.length} unrecognised name(s) retained`
              : ""}
          </p>
        )}

        {outcome.flags.length > 0 && (
          <ul className="space-y-0.5">
            {outcome.flags.map((f, i) => (
              <IngestFlagLine key={`${f.check_name ?? "flag"}-${i}`} flag={f} />
            ))}
          </ul>
        )}

        {outcome.row_warnings.length > 0 && (
          <ul className="space-y-0.5">
            {outcome.row_warnings.slice(0, 5).map((w, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                <Info className="h-3 w-3 mt-0.5 shrink-0" aria-hidden />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (outcome.state === "duplicate") {
    return (
      <div className="space-y-1 border-t pt-2">
        <div className="flex items-center gap-1.5 text-xs">
          <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="text-foreground font-medium">Already landed</span>
        </div>
        <p className="text-[11px] text-muted-foreground">
          These exact bytes are already recorded for this period and kind
          {outcome.ingest_status ? ` (${outcome.ingest_status})` : ""}, so nothing was re-parsed.
          Re-dropping is always safe.
        </p>
        {outcome.sha256 && (
          <p className="text-[11px] text-muted-foreground font-mono" title={outcome.sha256}>
            sha256 {outcome.sha256.slice(0, 16)}…
          </p>
        )}
      </div>
    );
  }

  // rejected | error — the whole file was refused; nothing landed.
  return (
    <div className="space-y-1 border-t pt-2">
      <div className="flex items-center gap-1.5 text-xs">
        <AlertTriangle className="h-3.5 w-3.5 text-destructive" aria-hidden />
        <span className="text-destructive font-medium">
          {outcome.state === "rejected" ? "Rejected" : "Could not be dropped"}
          {outcome.reject_gate ? ` — ${outcome.reject_gate}` : ""}
        </span>
      </div>
      <p className="text-[11px] text-destructive/90 break-words">
        {outcome.reject_reason ?? "No reason returned."}
      </p>
      <p className="text-[11px] text-muted-foreground">
        Nothing was landed from this file. Fix it and drop it again — retrying costs nothing.
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// The drop point.
// ----------------------------------------------------------------------------
export function GoldenDropZone({
  cycleId,
  periodLabel,
  asOfDate,
  className,
  allowedKinds,
}: {
  cycleId: string | null | undefined;
  periodLabel: string | null | undefined;
  asOfDate: string | null | undefined;
  className?: string;
  /**
   * The kinds this instance may land. Omit for the full list. Passing it is a GATE, not a
   * cosmetic filter — see the header note: it constrains the picker, the detector and the upload
   * queue alike, so no route into `kind` can reach the ingest with a kind outside it.
   */
  allowedKinds?: readonly string[];
}) {
  const [entries, setEntries] = useState<QueuedDrop[]>([]);
  const [basis, setBasis] = useState("GBP");
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const drop = useGoldenDrop(cycleId);

  const label = (periodLabel ?? "").trim();
  // The ingest constrains period_label to YYYY-MM / YYYY-Qn (it becomes a Storage object-key
  // segment and part of the drop's unique key). reporting_cycles has no such CHECK, so a
  // cycle CAN carry a label the ingest will refuse — say so up front instead of letting every
  // file fail with a 400.
  const labelUsable = PERIOD_LABEL_RX.test(label);
  const asOf = (asOfDate ?? "").slice(0, 10);
  const canDrop = labelUsable && !!asOf;

  // The one place the allow-list is resolved. Everything downstream — picker, group headings,
  // detector, upload queue — reads these, so there is no second definition to drift from it.
  const kindSpecs: GoldenKindSpec[] = allowedKinds
    ? GOLDEN_KIND_SPECS.filter((s) => allowedKinds.includes(s.kind))
    : GOLDEN_KIND_SPECS;
  const kindGroups: string[] = kindSpecs.reduce<string[]>((acc, s) => {
    if (!acc.includes(s.group)) acc.push(s.group);
    return acc;
  }, []);
  const isKindAllowed = useCallback(
    (k: string) =>
      allowedKinds ? allowedKinds.includes(k) : GOLDEN_KIND_SPECS.some((s) => s.kind === k),
    [allowedKinds],
  );

  // SUGGEST, NEVER DECIDE. The detected kind is written ONLY into a row that is still untagged,
  // so a human who picked while the file was being read always wins; an ambiguous detection
  // writes nothing at all; and no path here submits anything. A detection outside `allowedKinds`
  // is recorded but never pre-selected — the detector is not a way round the allow-list.
  const applyDetection = useCallback(
    (id: string, detection: KindDetection | null) => {
      setEntries((prev) =>
        prev.map((e) => {
          if (e.id !== id) return e;
          const preselect =
            !e.kind &&
            !!detection &&
            detection.confidence !== "ambiguous" &&
            !!detection.kind &&
            isKindAllowed(detection.kind);
          return { ...e, detecting: false, detection, kind: preselect ? detection.kind : e.kind };
        }),
      );
    },
    [isKindAllowed],
  );

  const addFiles = useCallback(
    (files: FileList | File[] | null) => {
      if (!files) return;
      const next = Array.from(files)
        .filter((f) => !!f)
        .map((file) => ({
          id: nextId(),
          file,
          kind: "",
          status: "queued" as const,
          outcome: null,
          detection: null,
          detecting: true,
        }));
      if (!next.length) return;
      setEntries((prev) => [...prev, ...next]);
      // Detection is advisory, so a failure to read the head is not an error the owner needs to
      // see — it just means the dropdown asks, exactly as it always did.
      for (const entry of next) {
        detectFileKind(entry.file).then(
          (d) => applyDetection(entry.id, d),
          () => applyDetection(entry.id, null),
        );
      }
    },
    [applyDetection],
  );

  // The LAST gate before the ingest, and the reason it is a function rather than a filter on the
  // picker alone: `kind` is set from three places (the picker, the detector's pre-selection, the
  // ambiguous shortlist) and read by four (runPending, retryOne, pendingCount, the row label). A
  // kind outside the allow-list must not be uploadable however it got into state.
  function isEntryDroppable(e: QueuedDrop): boolean {
    return (
      !!e.kind &&
      isKindAllowed(e.kind) &&
      e.outcome?.state !== "parsed" &&
      e.outcome?.state !== "duplicate"
    );
  }

  function setKind(id: string, kind: string) {
    // Refuse to record a kind this instance cannot land. Nothing in the UI can call this with
    // one today; if something later does, it fails closed instead of arming the drop button.
    if (!isKindAllowed(kind)) return;
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, kind } : e)));
  }

  function removeEntry(id: string) {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }

  // Queue the SAME file again with no kind — the one-file-two-kinds case (§8.1). The detection
  // is CARRIED OVER but the kind is deliberately NOT: re-selecting the kind that just landed
  // would only produce a duplicate. Carrying the detection keeps its shortlist on screen, which
  // is exactly the one-click "and now the other kind" this clone exists for.
  function cloneEntry(id: string) {
    setEntries((prev) => {
      const src = prev.find((e) => e.id === id);
      if (!src) return prev;
      return [
        ...prev,
        {
          id: nextId(),
          file: src.file,
          kind: "",
          status: "queued",
          outcome: null,
          detection: src.detection,
          detecting: false,
        },
      ];
    });
  }

  async function uploadOne(entry: QueuedDrop) {
    setEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, status: "reading", outcome: null } : e)),
    );
    let outcome: GoldenDropOutcome;
    try {
      const payload = await readCsvPayload(entry.file);
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, status: "uploading" } : e)));
      outcome = await drop({
        cycleId,
        periodLabel: label,
        asOfDate: asOf,
        kind: entry.kind,
        basis,
        filename: entry.file.name,
        payload,
      });
    } catch (e: any) {
      outcome = localDropError(entry.kind, `The file could not be read: ${e?.message ?? String(e)}`);
    }
    setEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, status: "done", outcome } : e)),
    );
  }

  // Sequential on purpose: each drop writes to the same immutable source table, and a
  // one-at-a-time run keeps the per-file result legible when several files fail differently.
  async function runPending() {
    if (!canDrop || running) return;
    const queue = entries.filter(isEntryDroppable);
    if (!queue.length) return;
    setRunning(true);
    try {
      for (const entry of queue) await uploadOne(entry);
    } finally {
      setRunning(false);
    }
  }

  async function retryOne(entry: QueuedDrop) {
    if (!canDrop || running || !entry.kind || !isKindAllowed(entry.kind)) return;
    setRunning(true);
    try {
      await uploadOne(entry);
    } finally {
      setRunning(false);
    }
  }

  const pendingCount = entries.filter(isEntryDroppable).length;
  const untaggedCount = entries.filter((e) => !e.kind).length;

  return (
    <div className={cn("space-y-3", className)}>
      {!labelUsable && (
        <Card className="p-3 border-destructive/40">
          <p className="font-body text-xs text-destructive">
            The period label on this cycle{" "}
            {label ? <span className="font-mono">({label})</span> : <span>(empty)</span>} cannot be
            used for a golden-CSV drop. The ingest requires YYYY-MM (2026-07) or YYYY-Qn (2026-Q2),
            because the label becomes part of the identity of every stored file. This cycle needs a
            label in one of those two shapes before anything can be dropped against it.
          </p>
        </Card>
      )}

      {/* Where the files land */}
      <Card
        className={cn(
          "p-6 border-dashed flex flex-col items-center text-center gap-2 transition-colors",
          dragging && "border-primary bg-primary/5",
          !canDrop && "opacity-60",
        )}
        onDragOver={(e) => {
          e.preventDefault();
          if (canDrop) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (canDrop) addFiles(e.dataTransfer?.files ?? null);
        }}
      >
        <UploadCloud className="h-7 w-7 text-muted-foreground" aria-hidden />
        <p className="font-body text-sm text-foreground">Drop the golden CSVs here</p>
        <p className="font-body text-xs text-muted-foreground max-w-md">
          Each file is read and identified from its own contents — check what it worked out, correct
          it if it is wrong, then drop. They land against{" "}
          <span className="font-mono">{label || "—"}</span>, as of {asOf || "—"} — taken from this
          cycle, never retyped.
        </p>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            // Reset so re-picking the same file fires change again (the two-kinds case).
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1"
          disabled={!canDrop || running}
          onClick={() => fileInput.current?.click()}
        >
          <FileUp className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          Choose files
        </Button>
      </Card>

      {entries.length > 0 && (
        <>
          {/* Batch controls */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1">
              <Label className="font-body text-xs text-muted-foreground">Currency basis</Label>
              <Select value={basis} onValueChange={setBasis} disabled={running}>
                <SelectTrigger className="h-8 w-56 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOLDEN_BASES.map((b) => (
                    <SelectItem key={b.value} value={b.value}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="font-body text-[11px] text-muted-foreground">
                Checked against the file's own currency line — a mismatch is rejected, not accepted.
              </p>
            </div>
            <Button size="sm" onClick={runPending} disabled={!canDrop || running || pendingCount === 0}>
              <UploadCloud className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              {running ? "Dropping…" : `Drop ${pendingCount} file${pendingCount === 1 ? "" : "s"}`}
            </Button>
          </div>

          {untaggedCount > 0 && (
            <p className="font-body text-[11px] text-muted-foreground">
              {untaggedCount} file{untaggedCount === 1 ? "" : "s"} still need
              {untaggedCount === 1 ? "s" : ""} to be tagged before {untaggedCount === 1 ? "it" : "they"} can be
              dropped — the contents did not settle it, so it is not guessed.
            </p>
          )}

          {/* One row per queued file */}
          <div className="space-y-2">
            {entries.map((entry) => {
              const spec = goldenKindSpec(entry.kind);
              const busy = entry.status === "reading" || entry.status === "uploading";
              const landed = entry.outcome?.state === "parsed" || entry.outcome?.state === "duplicate";
              return (
                <Card key={entry.id} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-body text-sm text-foreground truncate" title={entry.file.name}>
                        {entry.file.name}
                      </p>
                      <p className="font-body text-[11px] text-muted-foreground">
                        {formatBytes(entry.file.size)}
                        {entry.kind ? ` · ${goldenKindLabel(entry.kind)}` : " · not yet tagged"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {/* The one-file-two-kinds clone (§8.1) exists for the FE export that carries
                          portfolio AND benchmark returns in the same bytes. Under an allow-list
                          that excludes the FE kinds there is no such file here — each blotter file
                          is exactly one kind — so the button would offer a manoeuvre that cannot
                          apply, under a tooltip about an export this surface no longer lands. */}
                      {landed && !allowedKinds && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          disabled={running}
                          onClick={() => cloneEntry(entry.id)}
                          title="Queue the same file again under a different kind (one FE export carries both portfolio and benchmark returns)"
                        >
                          <Copy className="h-3 w-3 mr-1" aria-hidden />
                          Drop again as another kind
                        </Button>
                      )}
                      {entry.outcome && !landed && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          disabled={running || !entry.kind || !canDrop}
                          onClick={() => retryOne(entry)}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" aria-hidden />
                          Retry
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={running}
                        onClick={() => removeEntry(entry.id)}
                        aria-label={`Remove ${entry.file.name}`}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={entry.kind}
                      onValueChange={(v) => setKind(entry.id, v)}
                      disabled={busy || running}
                    >
                      <SelectTrigger className="h-8 w-full sm:w-[22rem] text-xs">
                        <SelectValue placeholder="What is this file?" />
                      </SelectTrigger>
                      <SelectContent>
                        {kindGroups.map((group) => (
                          <SelectGroup key={group}>
                            <SelectLabel>{group}</SelectLabel>
                            {kindSpecs.filter((s) => s.group === group).map((s) => (
                              <SelectItem key={s.kind} value={s.kind}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                    {busy && (
                      <span className="font-body text-[11px] text-muted-foreground">
                        {entry.status === "reading" ? "Reading…" : "Dropping…"}
                      </span>
                    )}
                  </div>

                  {!entry.outcome && (
                    <DetectionLine
                      detecting={entry.detecting}
                      detection={entry.detection}
                      kind={entry.kind}
                      disabled={busy || running}
                      isAllowed={isKindAllowed}
                      onPick={(k) => setKind(entry.id, k)}
                    />
                  )}

                  {spec && !entry.outcome && (
                    <p className="font-body text-[11px] text-muted-foreground">{spec.hint}</p>
                  )}

                  {entry.outcome && <DropOutcomePanel outcome={entry.outcome} />}
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
